import { FrontendConstants } from '../common/FrontendConstants';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { CommonConstants } from '@shared/common';
import type { ICallDTO, ICallOutcomeDTO, ICallTranscriptDTO } from '@shared/common';

/** Full detail for a single call: the call row plus its transcript and outcome, when present. */
export interface ICallDetailDTO {
  call: ICallDTO;
  transcript: ICallTranscriptDTO | null;
  outcome: ICallOutcomeDTO | null;
  summaryDisplay?: string; // The AI-generated summary translated into the requested viewer language; only set when a `lang` differing from the tenant's own locale is requested
}

/**
 * Client for call history and detail.
 *
 * - Tenant-facing reads route through the lobby service dashboard surface (`dashboard/:tenantID/calls`).
 * - Cross-tenant admin reads (Portal) route through the lobby service operator surface (`admin/calls`).
 */
export class CallClient {
  /** Singleton instance. */
  private static instance: CallClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton CallClient instance. */
  public static GetInstance(): CallClient {
    return CallClient.instance ?? (CallClient.instance = new CallClient());
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/calls - List a tenant's calls, newest first, with optional free-text search and pagination.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param offset - Number of rows to skip.
   * @param limit - Maximum number of rows.
   * @param pattern - Optional free-text search term (caller/summary/intent/status).
   * @returns The tenant's calls page, most recent first.
   */
  public async ListCalls(
    signal: AbortSignal,
    tenantID: string,
    offset: number = CommonConstants.Pagination.Offset,
    limit: number = CommonConstants.Pagination.Limit,
    pattern?: string
  ): Promise<ICallDTO[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (pattern) params.append('pattern', pattern);
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/calls?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch calls');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/calls/count - Count a tenant's calls matching an optional free-text search term and an optional recency floor on the call start time.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param pattern - Optional free-text search term.
   * @param sinceISO - Optional ISO timestamp; only calls started at or after it are counted.
   * @returns The number of matching calls.
   */
  public async CountCalls(signal: AbortSignal, tenantID: string, pattern?: string, sinceISO?: string): Promise<number> {
    const params = new URLSearchParams();
    if (pattern) params.append('pattern', pattern);
    if (sinceISO) params.append(CommonConstants.Params.Since, sinceISO);
    const query = params.toString();
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/calls/count${query ? `?${query}` : ''}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch call count');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/calls/:callID - Fetch one call with transcript and outcome.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param callID - The call to fetch.
   * @param lang - Optional viewer language (BCP-47); when set, the response includes `summaryDisplay` with the AI summary translated into this language.
   * @returns The call detail.
   */
  public async GetCallDetail(signal: AbortSignal, tenantID: string, callID: string, lang?: string): Promise<ICallDetailDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const query = lang ? `?${new URLSearchParams({ [CommonConstants.Params.Lang]: lang }).toString()}` : '';
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/calls/${callID}${query}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch call detail');
    return await res.json();
  }

  /**
   * POST /api/lobby/dashboard/:tenantID/calls/:callID/resend-sms - Re-send the owner SMS for a call.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param callID - The call whose notification to re-send.
   */
  public async ResendSMS(signal: AbortSignal, tenantID: string, callID: string): Promise<void> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/calls/${callID}/resend-sms`, { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to resend SMS');
  }

  /**
   * GET /api/lobby/admin/calls - List recent calls across all tenants, optionally filtered to one tenant (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant whose calls to list (required).
   * @param offset - Number of rows to skip.
   * @param limit - Maximum number of rows.
   * @param pattern - Optional free-text search term (caller/summary/intent/status).
   * @returns The tenant's calls page, most recent first.
   */
  public async ListAllCalls(
    signal: AbortSignal,
    tenantID: string,
    offset: number = CommonConstants.Pagination.Offset,
    limit: number = CommonConstants.Pagination.Limit,
    pattern?: string
  ): Promise<ICallDTO[]> {
    const params = new URLSearchParams({ tenantID, limit: limit.toString(), offset: offset.toString() });
    if (pattern) params.append('pattern', pattern);
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/calls?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch calls');
    return await res.json();
  }

  /**
   * GET /api/lobby/admin/calls/count - Count calls (Portal operator only). With a tenantID, counts that tenant's matching calls; without one, returns the platform-wide total (operator summary).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - Optional tenant whose calls to count; omit for the platform-wide total.
   * @param pattern - Optional free-text search term (only applied with a tenantID).
   * @returns The number of matching calls.
   */
  public async CountAllCalls(signal: AbortSignal, tenantID?: string, pattern?: string): Promise<number> {
    const params = new URLSearchParams();
    if (tenantID) params.append('tenantID', tenantID);
    if (pattern) params.append('pattern', pattern);
    const query = params.toString();
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/calls/count${query ? `?${query}` : ''}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch call count');
    return await res.json();
  }

  /**
   * GET /api/lobby/admin/clear/calls - Flush the cross-tenant calls Redis cache (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'CLEARED' confirmation string.
   */
  public async ClearCalls(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'admin/clear/calls', { signal }, auth);
    if (!res.ok) throw new Error('Failed to clear call cache');
    return await res.text();
  }

  /**
   * GET /api/lobby/admin/calls/:tenantID/:callID - Fetch one call with detail (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param callID - The call to fetch.
   * @returns The call detail.
   */
  public async GetAdminCallDetail(signal: AbortSignal, tenantID: string, callID: string): Promise<ICallDetailDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/calls/${tenantID}/${callID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch call detail');
    return await res.json();
  }

  /**
   * POST /api/lobby/admin/calls/:tenantID/:callID/resend-sms - Re-send the owner SMS for a call on behalf of the tenant (Portal admin only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param callID - The call whose notification to re-send.
   */
  public async AdminResendSMS(signal: AbortSignal, tenantID: string, callID: string): Promise<void> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/calls/${tenantID}/${callID}/resend-sms`, { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to resend SMS');
  }
}
